<template>
  <div class="flex flex-col">
    <div class="text-black text-2xl font-medium mb-3">
      Check Top Up Idle
    </div>

    <BCard>
      <div class="flex flex-wrap gap-[12px] items-center justify-end mb-2">
        <div class="flex flex-wrap gap-[12px] items-center justify-end">
          <b-dropdown
            variant="outline-secondary"
            toggle-class="!flex items-center gap-[6px] border-[#F95031] bg-white px-[12px] py-[6px] rounded-[6px]"
            no-caret
          >
            <template #button-content>
              <span class="text-[14px] font-semibold">{{ getFilterLabel(statusOptions, statusFilter, 'Status') }}</span>
              <feather-icon
                icon="ChevronDownIcon"
                size="14"
                class="ml-1"
              />
            </template>
            <b-dropdown-item
              v-for="option in statusOptions"
              :key="option.value"
              @click="statusFilter = option.value"
            >
              {{ option.label }}
            </b-dropdown-item>
          </b-dropdown>

          <b-dropdown
            variant="outline-secondary"
            toggle-class="!flex items-center gap-[6px] border-[#F95031] bg-white px-[12px] py-[6px] rounded-[6px]"
            no-caret
          >
            <template #button-content>
              <span class="text-[14px] font-semibold">{{ getFilterLabel(userLevelOptions, userLevelFilter, 'User Level') }}</span>
              <feather-icon
                icon="ChevronDownIcon"
                size="14"
                class="ml-1"
              />
            </template>
            <b-dropdown-item
              v-for="option in userLevelOptions"
              :key="option.value"
              @click="userLevelFilter = option.value"
            >
              {{ option.label }}
            </b-dropdown-item>
          </b-dropdown>

          <div class="w-[280px]">
            <b-input-group
              class="input-group-merge"
              size="md"
            >
              <b-input-group-prepend is-text>
                <feather-icon icon="SearchIcon" />
              </b-input-group-prepend>
              <b-form-input
                v-model="search"
                type="search"
                placeholder="Search last 4 digits / name / email"
                debounce="500"
                @input="onChangeSearch"
              />
            </b-input-group>
          </div>

          <date-range-picker
            ref="picker"
            v-model="dateRange"
            :locale-data="locale"
            :show-ranges="false"
            :ranges="false"
            :opens="'left'"
            control-container-class="border rounded"
          >
            <template v-slot:input="picker">
              <div
                class="d-flex btn justify-content-between align-items-center gap-5"
                style="height: 32px;"
              >
                <span
                  v-if="!isDateChanged"
                  class="d-flex justify-content-start text-[#A8A8A8] font-normal"
                >
                  Start date &nbsp; ⇀ &nbsp; End date
                </span>
                <span
                  v-else
                  class="d-flex justify-content-start text-black font-medium"
                >
                  {{ YEAR_MONTH_DAY(picker.startDate) }} ⇀ {{ YEAR_MONTH_DAY(picker.endDate) }}
                </span>
                <img
                  src="https://storage.googleapis.com/komerce/assets/komerce-icon/Hitam/calendar.svg"
                  alt="calendar"
                  width="15px"
                  class="d-flex justify-content-end"
                >
              </div>
            </template>
          </date-range-picker>
        </div>
      </div>

      <BOverlay
        :show="loadingTable"
        spinner-variant="primary"
        variant="light"
        blur="0"
        opacity=".5"
        rounded="sm"
      >
        <div
          id="table"
          class="mt-1"
          style="height: calc(100vh - 280px); overflow-y: scroll;"
        >
          <BTable
            :items="items"
            :fields="fields"
            show-empty
            empty-text="Tidak ada data"
            hover
            selectable
            select-mode="single"
          >
            <template #cell(partner)="data">
              <div
                v-if="data.item.user"
                class="flex flex-col"
              >
                <div class="flex items-center gap-[6px]">
                  <span class="text-black text-nowrap">
                    {{ data.item.user.full_name }}
                  </span>
                  <img
                    v-if="data.item.user.badge_url"
                    :src="data.item.user.badge_url"
                    alt="badge"
                    class="w-6 ml-[5px]"
                  >
                </div>
                <span class="text-[#828282] text-[12px]">
                  {{ data.item.user.email }}
                </span>
              </div>
            </template>

            <template #cell(cards)="data">
              <div
                v-if="data.item.card"
                class="flex items-center gap-[6px]"
              >
                <i class="k-card text-[20px] text-[#828282]" />
                <span class="text-nowrap">
                  {{ data.item.card.card_label }} ***{{ data.item.card.last_number || '' }}
                </span>
                <BButton
                  variant="flat"
                  class="p-0 w-[20px] h-[20px] flex items-center justify-center"
                  @click="copyToClipboard(data.item.card.card_id)"
                >
                  <img
                    src="https://storage.googleapis.com/komerce/assets/komerce-icon/Orange/copy.svg"
                    alt="copy"
                    class="w-[16px]"
                  >
                </BButton>
              </div>
            </template>

            <template #cell(status)="data">
              <div
                class="flex items-center gap-[6px] text-[14px] font-semibold"
                :class="getStatusColorClass(data.item.status)"
              >
                <span
                  :class="getStatusIconClass(data.item.status)"
                  class="text-[16px]"
                />
                <span>{{ formatStatusLabel(data.item.status) }}</span>
              </div>
            </template>

            <template #cell(time)="data">
              <div class="text-[13px] text-nowrap">
                {{ formatTime(data.item.date) }}
              </div>
            </template>

            <template #cell(nominal)="data">
              <span class="text-[#27AE60] font-semibold text-nowrap">
                {{ IDR(data.item.amount) }}
              </span>
            </template>

            <template #cell(action)="data">
              <div class="flex items-center gap-[8px] justify-center">
                <template v-if="isIdleStatus(data.item.status)">
                  <BButton
                    v-b-tooltip.hover.top="'Cancel Top Up'"
                    variant="flat"
                    class="!bg-[#ffeded] text-primary hover:bg-[#FCE3E3] p-0 w-[32px] h-[32px] !flex items-center justify-center rounded-[6px]"
                    @click="openModalConfirm('refund', data.item)"
                  >
                    <feather-icon
                      icon="ArrowLeftIcon"
                      size="16"
                    />
                  </BButton>

                  <BButton
                    v-b-tooltip.hover.top="'Check Top Up'"
                    variant="flat"
                    class="!bg-[#e6f6ec] text-success hover:bg-[#d4f0df] p-0 w-[32px] h-[32px] !flex items-center justify-center rounded-[6px]"
                    @click="openModalConfirm('check', data.item)"
                  >
                    <feather-icon
                      icon="ArrowRightIcon"
                      size="16"
                    />
                  </BButton>
                </template>
                <div
                  v-else-if="isFailedStatus(data.item.status)"
                  class="bg-[#ffeded] text-[#E12A2A] px-[16px] py-[6px] rounded-[6px] font-semibold text-[14px] text-center"
                >
                  Refund
                </div>
                <div
                  v-else-if="isSuccessStatus(data.item.status)"
                  class="bg-[#e6f6ec] text-[#27AE60] px-[16px] py-[6px] rounded-[6px] font-semibold text-[14px] text-center"
                >
                  Success
                </div>
              </div>
            </template>
          </BTable>
        </div>
      </BOverlay>

      <BRow class="mt-1">
        <BCol
          cols="12"
          class="flex flex-col md:flex-row justify-between bg-[#fafafa] items-center rounded p-1 gap-[12px] md:gap-0"
        >
          <div class="flex flex-wrap items-center justify-center md:justify-start gap-[12px]">
            <span class="text-black">{{ `Total ${totalRows} Data` }}</span>
            <div class="bg-light d-flex justify-content-center align-items-center p-50 rounded">
              <span class="text-black mr-50">List per page: </span>
              <BButton
                v-for="page in optionsPage"
                :key="page"
                class="btn-icon"
                size="sm"
                :variant="totalPerPage === page ? 'primary' : 'flat-dark'"
                @click="setPerPage(page)"
              >
                {{ page }}
              </BButton>
            </div>
          </div>

          <BPagination
            v-model="currentPage"
            :total-rows="totalRows"
            :per-page="totalPerPage"
            first-number
            last-number
            class="pagination-primary mb-0"
          >
            <template #prev-text>
              <feather-icon
                size="18"
                icon="ChevronLeftIcon"
              />
            </template>
            <template #next-text>
              <feather-icon
                size="18"
                icon="ChevronRightIcon"
              />
            </template>
          </BPagination>
        </BCol>
      </BRow>
    </BCard>

    <ModalConfirm
      :visible="visibleModalConfirm"
      :trigger="modalTrigger"
      :item="selectedItem"
      @close="visibleModalConfirm = false"
      @confirm="handleConfirmAction"
    />
  </div>
</template>

<script>
import {
  LABELDAY,
  LABELMONTH,
  DAY_MONTH_YEAR,
  YEAR_MONTH_DAY,
} from '@/libs/filterDate'
import { newAxiosIns } from '@/libs/axios'
import { IDR } from '@/libs/currency'
import moment from 'moment'
import _ from 'lodash'
import DateRangePicker from 'vue2-daterange-picker'
import 'vue2-daterange-picker/dist/vue2-daterange-picker.css'
import { today, komcardsDate } from '@/store/helpers'
import { VBTooltip } from 'bootstrap-vue'
import {
  columnsConfig,
  statusOptions,
  userLevelOptions,
  statusColorMap,
  statusIconMap,
  OPTIONS_PAGE,
} from './config'
import ModalConfirm from './ModalConfirm.vue'

export default {
  name: 'MonitoringCheckTopUpIdle',
  components: {
    DateRangePicker,
    ModalConfirm,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  data() {
    return {
      IDR,
      DAY_MONTH_YEAR,
      YEAR_MONTH_DAY,
      moment,
      loadingTable: false,
      search: '',
      statusFilter: 'idle',
      userLevelFilter: 'all',
      statusOptions,
      userLevelOptions,
      dateRange: {
        startDate: komcardsDate,
        endDate: today,
      },
      locale: {
        format: 'dd/mm/yyyy',
        daysOfWeek: LABELDAY,
        monthNames: LABELMONTH,
      },
      fields: columnsConfig,
      items: [],
      currentPage: 1,
      optionsPage: OPTIONS_PAGE,
      totalPerPage: 25,
      totalRows: 0,
      visibleModalConfirm: false,
      modalTrigger: 'cancel',
      selectedItem: null,
      isDateChanged: false,
    }
  },
  watch: {
    $route: {
      handler: 'getMissingList',
      immediate: true,
    },
    statusFilter() {
      this.currentPage = 1
      this.getMissingList()
    },
    userLevelFilter() {
      this.currentPage = 1
      this.getMissingList()
    },
    dateRange() {
      this.isDateChanged = true
      this.currentPage = 1
      this.getMissingList()
    },
    currentPage() {
      this.getMissingList()
    },
  },
  methods: {
    async getMissingList() {
      this.loadingTable = true
      const url = 'komcards/api/v1/transactions/topup-idle'
      try {
        const { data } = await newAxiosIns.get(url, {
          params: {
            page: this.currentPage,
            per_page: this.totalPerPage,
            start_date: moment(this.dateRange.startDate).format('YYYY-MM-DD'),
            end_date: moment(this.dateRange.endDate).format('YYYY-MM-DD'),
            status: this.statusFilter === 'all' ? '' : this.statusFilter,
            user_level: this.userLevelFilter === 'all' ? '' : this.userLevelFilter,
            search: this.search,
          },
        })
        this.items = data?.data || []
        this.totalRows = data?.meta?.pagination?.total_row || 0
      } catch (err) {
        this.$toast_error({ message: err.response?.data?.meta?.message || err.response?.data?.message || 'Gagal mengambil data' })
      } finally {
        this.loadingTable = false
      }
    },
    async checkTopUp(item, trigger) {
      const url = 'komcards/api/v1/transactions/topup-idle'
      const params = {
        id: item.id,
        type: trigger,
      }
      try {
        const { data } = await newAxiosIns.post(url, params)
        this.$toast_success({ message: data?.meta?.message || 'Data berhasil diubah' })
        this.getMissingList()
      } catch (err) {
        this.$toast_error({ message: err.response?.data?.meta?.message || err.response?.data?.message || 'Gagal mengambil data' })
      }
    },
    /**
     * Retrieves the label for a given filter value from an options array.
     * Used by both status and user level dropdowns.
     */
    getFilterLabel(options, value, fallback) {
      const option = options.find(opt => opt.value === value)
      return option ? option.label : fallback
    },

    onChangeSearch: _.debounce(function search() {
      const trimmedSearch = this.search.trim()
      this.search = trimmedSearch
      this.currentPage = 1
      this.getMissingList()
    }, 1000),

    // --- Status display helpers ---
    normalizeStatus(status) {
      return status ? status.toLowerCase() : ''
    },
    getStatusColorClass(status) {
      return statusColorMap[this.normalizeStatus(status)] || 'text-gray-600'
    },
    getStatusIconClass(status) {
      return statusIconMap[this.normalizeStatus(status)] || 'k-status'
    },
    formatStatusLabel(status) {
      if (!status) return ''
      return status.charAt(0).toUpperCase() + status.slice(1)
    },
    isIdleStatus(status) {
      return this.normalizeStatus(status) === 'idle'
    },
    isFailedStatus(status) {
      return this.normalizeStatus(status) === 'failed'
    },
    isSuccessStatus(status) {
      return this.normalizeStatus(status) === 'success'
    },

    // --- Utility methods ---
    copyToClipboard(text) {
      navigator.clipboard.writeText(text).then(() => {
        this.$toast_success({ message: `${text} Copied` })
      })
    },
    formatTime(time) {
      if (!time) return ''
      return moment(time).format('DD MMMM YYYY - HH:mm')
    },
    setPerPage(page) {
      this.totalPerPage = page
      this.currentPage = 1
      this.getMissingList()
    },

    // --- Modal actions ---
    openModalConfirm(trigger, item) {
      this.modalTrigger = trigger
      this.selectedItem = item
      this.visibleModalConfirm = true
    },
    handleConfirmAction({ trigger, item }) {
      this.visibleModalConfirm = false
      this.checkTopUp(item, trigger)
    },
  },
}
</script>
